import { useState } from "react";
import { Box, Palette, Stack, Tooltip, Typography } from "@mui/material";
import { ChevronDown, ChevronUp, ExternalLink, FileText } from "lucide-react";
import Label from "@components/Label";
import {
  getWorkflowStatusColor,
  TApprovalStatusString,
} from "@pages/DocumentsV2/utils/getStatusColor";
import { IReferencedDoc } from "@pages/askDost/types";
import { previewDocumentHandle } from "@pages/askDost/services/askDostService";
import { borderGeneralStyle } from "@utils/cssStyles";
import { DocumentPreviewModal } from "./DocumentPreviewModal";

interface IReferencedDocsChipsProps {
  docs: IReferencedDoc[];
  palette: Palette;
  translate: (text: string, options?: any) => string;
}

export const ReferencedDocsChips: React.FC<IReferencedDocsChipsProps> = ({
  docs,
  palette,
  translate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any | null>(null);

  const [approvalStatusByHandle, setApprovalStatusByHandle] = useState<
    Record<string, TApprovalStatusString | null>
  >({});
  const [pendingHandles, setPendingHandles] = useState<Set<string>>(new Set());

  if (!docs?.length) return null;

  const handleDocClick = (doc: any) => {
    if (doc?.url) {
      window.open(doc.url, "_blank", "noopener,noreferrer");
      return;
    }

    if (doc?.handle || doc?.id) {
      setSelectedDoc(doc);
    }
  };

  const handleHoverDoc = async (doc: any) => {
    const handle = doc?.handle;
    if (
      !handle ||
      handle in approvalStatusByHandle ||
      pendingHandles.has(handle)
    ) {
      return;
    }

    setPendingHandles((prev) => new Set(prev).add(handle));
    try {
      const response = await previewDocumentHandle(handle);
      const data = response.ok ? await response.json() : null;
      setApprovalStatusByHandle((prev) => ({
        ...prev,
        [handle]: data?.approvalStatus ?? null,
      }));
    } catch (error) {
      console.error("Error fetching document approval status:", error);
      setApprovalStatusByHandle((prev) => ({ ...prev, [handle]: null }));
    } finally {
      setPendingHandles((prev) => {
        const next = new Set(prev);
        next.delete(handle);
        return next;
      });
    }
  };

  return (
    <>
      <Stack spacing={1} alignItems="flex-start">
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            ...borderGeneralStyle,
            borderRadius: 999,
            px: 1.5,
            py: 1,
            cursor: "pointer",
            "&:hover": {
              borderColor: palette.secondary.dark,
              backgroundColor: palette.text.input,
            },
          }}
        >
          <FileText size={12} color={palette.text.label} />
          <Typography variant="caption" sx={{ color: palette.text.label }}>
            {translate("documents_referenced")} ({docs.length})
          </Typography>
          {isOpen ? (
            <ChevronUp size={12} color={palette.text.label} />
          ) : (
            <ChevronDown size={12} color={palette.text.label} />
          )}
        </Stack>

        {isOpen && (
          <Box
            sx={{
              width: "100%",
              ...borderGeneralStyle,
              borderRadius: 1.5,
              overflow: "hidden",
            }}
          >
            {docs?.map((doc: any, index) => {
              const approvalStatus = doc?.handle
                ? approvalStatusByHandle[doc.handle]
                : undefined;
              const isLoadingApproval = doc?.handle
                ? pendingHandles.has(doc.handle)
                : false;

              const row = (
                <Stack
                  key={doc?.name || index}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  onClick={() => handleDocClick(doc)}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom:
                      index === docs.length - 1
                        ? "none"
                        : `1px solid ${palette.text.borderLine}`,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: palette.text.input },
                  }}
                >
                  <FileText
                    size={14}
                    color={palette.text.label}
                    style={{ flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        display: "block",
                        fontWeight: 600,
                        color: palette.text.heading,
                      }}
                    >
                      {doc?.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        display: "block",
                        color: palette.text.label,
                      }}
                    >
                      {doc?.url ||
                        doc?.handle ||
                        translate("unable_to_list_too_many_documents")}
                    </Typography>
                  </Box>
                  {doc?.url && (
                    <ExternalLink
                      size={16}
                      color={palette.secondary.dark}
                      style={{ flexShrink: 0 }}
                    />
                  )}
                </Stack>
              );

              if (!doc?.handle) return row;

              return (
                <Tooltip
                  key={doc?.name || index}
                  placement="right"
                  arrow
                  onOpen={() => handleHoverDoc(doc)}
                  title={
                    isLoadingApproval ? (
                      translate("loading")
                    ) : approvalStatus ? (
                      <Label
                        variant="ghost"
                        color={getWorkflowStatusColor(approvalStatus)}
                        sx={{ fontWeight: 600 }}
                      >
                        {translate(approvalStatus)}
                      </Label>
                    ) : (
                      ""
                    )
                  }
                >
                  {row}
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Stack>

      <DocumentPreviewModal
        doc={selectedDoc}
        onClose={() => setSelectedDoc(null)}
        palette={palette}
      />
    </>
  );
};
